/* eslint-disable no-underscore-dangle */
import { useEffect, useState } from 'react';
import AccountsService from '../Services/AccountsService';

// Tiny in-memory cache so we don't refetch /accounts on every modal
// open / every Payments table row. The cache is a module-level promise
// — the first caller starts the fetch, every subsequent caller awaits
// the same promise. Components that need the resolved list use the
// `useAccountsList` hook below.
//
// The intentional simplification: there is no TTL and no automatic
// refresh. The Accounts admin page is responsible for calling
// `invalidateAccountsCache()` after a create/edit so other surfaces
// pick up the change next render. For surfaces opened before an edit,
// re-opening triggers no refetch — but the displayed `account_id`
// labels only change in response to admin edits which are very
// infrequent, so the staleness window is acceptable.

let cachedPromise = null;

export function getAccountsCached() {
  if (!cachedPromise) {
    cachedPromise = AccountsService.list({}).catch((err) => {
      // Don't cache a rejected promise — let the next caller retry.
      cachedPromise = null;
      throw err;
    });
  }
  return cachedPromise;
}

export function invalidateAccountsCache() {
  cachedPromise = null;
}

// Convenience hook: returns { accounts, byId, loading, error, reload }.
// `byId` is a Map<account_id, Account> for O(1) lookups in tables.
// `reload` invalidates the cache and re-fetches — used by the Accounts
// admin page after a successful create/edit.
export function useAccountsList() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bumping `version` triggers a fresh resolve. We pair it with a
  // cancelled flag so a stale fetch can't overwrite a newer one when
  // the consumer calls reload() in quick succession.
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAccountsCached()
      .then((list) => {
        if (cancelled) return;
        setAccounts(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setAccounts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [version]);

  const byId = new Map();
  accounts.forEach((a) => {
    if (a && a._id) byId.set(a._id, a);
  });

  const reload = () => {
    invalidateAccountsCache();
    setVersion((v) => v + 1);
  };

  return {
    accounts, byId, loading, error, reload,
  };
}
